import React, { Fragment } from "react";
import { Box, Typography } from "@mui/material";
import {
  AutoAwesomeOutlined,
  BuildOutlined,
  DeveloperBoardOutlined,
  DnsOutlined,
  EnergySavingsLeafOutlined,
  HomeWorkOutlined,
  InsightsOutlined,
  IntegrationInstructionsOutlined,
  LanguageOutlined,
  LocalShippingOutlined,
  MemoryOutlined,
  PowerOutlined,
  PrecisionManufacturingOutlined,
  PublicOutlined,
  SensorsOutlined,
  StarsOutlined,
  StorageOutlined,
  UnfoldMoreOutlined,
  VerifiedOutlined,
} from "@mui/icons-material";

import ContentSection from "../components/ContentSection";
import CompactCard from "../components/CompactCard";
import IconedItemsList from "../components/ui/IconedItemsList";
import LinkButton from "../components/ui/LinkButton";

import ConnectionLinesInTheSunset from "../assets/images/ConnectionLinesInTheSunset.jpg";
import Wires from "../assets/images/Wires.jpg";
import LinesOfCode from "../assets/images/LinesOfCode.jpg";
import FactorySetting from "../assets/images/FactorySetting.jpg";
import ItInfrastuctureCityScape from "../assets/images/ItInfrastuctureCityScape.jpg";
import SolarPanels from "../assets/images/SolarPanels.jpg";
import BackgroundImageBox from "../components/ui/BackgroundImageBox";
import HeroSectionV2 from "../components/HeroSectionV2";

function EnergyPage() {
  return (
    <Fragment>
      <HeroSectionV2
        heading="Empowering Sustainable Energy"
        subheading="With Advanced Technology"
        dark
      >
        <Box display="flex" gap={6} flexDirection={{ xs: "column", lg: "row" }}>
          <Box sx={{ flex: 1, flexShrink: 0 }}>
            <Typography
              color="#001b35"
              variant="h4"
              sx={{ flex: 1, flexShrink: 0 }}
            >
              Revolutionising Data and Energy Infrastructure
            </Typography>
            <Typography color="#001b35" variant="body1" marginTop={2}>
              Flamestone combines over a decade of expertise in hardware design,
              software development, and innovation to drive sustainable energy
              solutions. From cutting-edge server technology and AI-driven
              systems to customised software, Flamestone is at the forefront of
              integrating advanced technology into energy, logistics and
              industrial applications.
            </Typography>
            <Typography color="#001b35" variant="body1" marginTop={2}>
              Our mission is to empower industries to optimise operations,
              reduce emissions, and achieve sustainable growth.
            </Typography>
          </Box>
          <Box
            sx={{
              display: { xs: "none", lg: "block" },
              flex: 1,
              backgroundImage: `url(${ConnectionLinesInTheSunset})`,
              backgroundSize: "cover",
              backgroundPosition: "center",
              borderRadius: 8,
              minHeight: "200px",
            }}
          />
        </Box>
      </HeroSectionV2>
      <BackgroundImageBox image={Wires}>
        <Box sx={{ flex: 1, flexShrink: 0 }}>
          <Typography color="#001b35" variant="h2" marginBlockStart={2}>
            Advanced Data Centre and Hardware Solutions
          </Typography>
          <Typography
            color="#001b35"
            variant="body1"
            marginTop={2}
          >
            Flamestone offers state-of-the-art server technology and custom
            hardware development designed to meet the unique challenges of the
            energy sector. Leveraging partnerships with leading manufacturers
            like <b>Foxconn, Pegatron, and Flextronics</b>, we deliver tailored
            solutions to ensure efficiency and scalability.
          </Typography>
        </Box>
        <Typography color="#001b35" variant="h6" marginBlockStart={6}>
          Our Offerings
        </Typography>
        <Box
          sx={{
            display: "flex",
            flex: 1,
            flexDirection: { xs: "column", lg: "row" },
            flexShrink: 0,
            gap: 4,
            marginTop: 4,
          }}
        >
          {[
            {
              heading: "Computer Servers",
              description:
                "High-performance systems for processing energy usage simulations and real-time analytics.",
              icon: DnsOutlined,
            },
            {
              heading: "Storage Servers",
              description:
                "Reliable solutions for managing large datasets, critical for energy system monitoring and reporting.",
              icon: StorageOutlined,
            },
            {
              heading: "AI Servers",
              description:
                "Powerful platforms for advanced modelling, predictive analytics, and emissions monitoring in biogas and biomethane facilities.",
              icon: MemoryOutlined,
            },
            {
              heading: "GPU Servers",
              description:
                "Ideal for handling complex data visualisation and machine learning applications for IoT-enabled energy systems.",
              icon: DeveloperBoardOutlined,
            },
          ].map(({ heading, description, icon }) => (
            <Box
              key={heading}
              boxSizing="border-box"
              width={{ sx: "100%", lg: "25%" }}
            >
              <CompactCard
                key={heading}
                icon={icon}
                title={heading}
                description={description}
                sx={{ textAlign: "left" }}
              />
            </Box>
          ))}
        </Box>
      </BackgroundImageBox>
      <Box
        backgroundColor="#E8F5E9"
        sx={{ marginBottom: { xs: "200px", lg: "150px" } }}
      >
        <ContentSection>
          <Box
            sx={{
              padding: { xs: 0, lg: 6 },
              display: "flex",
              flexDirection: { xs: "column", lg: "row" },
              justifyContent: "center",
              gap: 6,
            }}
          >
            <Box sx={{ flex: 1, flexShrink: 0 }}>
              <Typography color="#001b35" variant="h6">
                Key Benefits
              </Typography>
              <IconedItemsList
                marginBlockStart={4}
                textAlign="left"
                items={[
                  {
                    heading: "Customisation",
                    description:
                      "Hardware tailored to specific industry needs.",
                    icon: BuildOutlined,
                  },
                  {
                    heading: "Scalability",
                    description:
                      "Designed for small to large-scale operations, ensuring flexibility and adaptability.",
                    icon: UnfoldMoreOutlined,
                  },
                  {
                    heading: "Energy Efficiency",
                    description:
                      "Built to align with sustainability goals, reducing operational costs and environmental impact.",
                    icon: EnergySavingsLeafOutlined,
                  },
                ]}
              />
            </Box>
            <Box sx={{ flex: 1, flexShrink: 0 }}>
              <img
                alt=""
                src={ItInfrastuctureCityScape}
                style={{
                  borderRadius: 32,
                  marginBottom: "-200px",
                  height: "450px",
                  width: "100%",
                  objectFit: "cover",
                }}
              />
            </Box>
          </Box>
        </ContentSection>
      </Box>
      <BackgroundImageBox image={LinesOfCode}>
        <Box sx={{ flex: 1, flexShrink: 0 }}>
          <Typography color="#001b35" variant="h2" marginBlockStart={2}>
            Customised Software for Smarter Operations
          </Typography>
          <Typography
            color="#001b35"
            variant="body1"
            marginTop={2}
          >
            Flamestone’s expertise extends into software development, creating
            fully integrated platforms that optimise energy systems and
            industrial workflows.
          </Typography>
        </Box>
        <Typography color="#001b35" variant="h6" marginBlockStart={6}>
          Capabilities
        </Typography>
        <Box
          sx={{
            display: "flex",
            flex: 1,
            flexDirection: { xs: "column", lg: "row" },
            flexShrink: 0,
            gap: 4,
            marginTop: 4,
          }}
        >
          {[
            {
              heading: "AI-Driven Analytics",
              description:
                "Predictive maintenance and optimisation for energy production facilities.",
              icon: InsightsOutlined,
            },
            {
              heading: "IoT Integration",
              description:
                "Smart sensors and real-time data visualisation to enhance monitoring and operational efficiency.",
              icon: SensorsOutlined,
            },
            {
              heading: "Deep Localisation",
              description:
                "Multilingual and region-specific software customisation for seamless global deployment.",
              icon: LanguageOutlined,
            },
            {
              heading: "Automation Tools",
              description:
                "Streamlining energy processes to reduce waste, increase productivity, and improve sustainability outcomes.",
              icon: AutoAwesomeOutlined,
            },
          ].map(({ heading, description, icon }) => (
            <Box
              key={heading}
              boxSizing="border-box"
              width={{ sx: "100%", lg: "25%" }}
            >
              <CompactCard
                key={heading}
                icon={icon}
                title={heading}
                description={description}
                sx={{ textAlign: "left" }}
              />
            </Box>
          ))}
        </Box>
      </BackgroundImageBox>
      <BackgroundImageBox image={FactorySetting}>
        <Box sx={{ flex: 1, flexShrink: 0 }}>
          <Typography color="#001b35" variant="h2" marginBlockStart={2}>
            Flamestone’s Expertise in R&D and Manufacturing
          </Typography>
          <Typography
            color="#001b35"
            variant="body1"
            marginTop={2}
          >
            Flamestone’s global R&D network, paired with our manufacturing
            partnerships, ensures reliable and cutting-edge solutions. We
            collaborate with <b>Microsoft, Intel, and TUV-SUD</b> for testing
            and certification to guarantee compliance with global standards.
          </Typography>
        </Box>
        <Typography color="#001b35" variant="h6" marginBlockStart={6}>
          Key Highlights
        </Typography>
        <Box
          sx={{
            display: "flex",
            flex: 1,
            flexDirection: { xs: "column", lg: "row" },
            flexShrink: 0,
            gap: 4,
            marginTop: 4,
          }}
        >
          {[
            {
              heading: "Global Research Facilities",
              description:
                "Locations in Europe and Asia focus on energy efficiency, hardware optimisation, and AI integration.",
              icon: PublicOutlined,
            },
            {
              heading: "High-Quality Standards",
              description:
                "Comprehensive testing and certification processes, including CE, RoHS, and Google GMS compliance.",
              icon: VerifiedOutlined,
            },
            {
              heading: "Prototyping and Production",
              description:
                "Rapid deployment of innovative solutions without compromising on quality or reliability.",
              icon: PrecisionManufacturingOutlined,
            },
          ].map(({ heading, description, icon }) => (
            <Box
              key={heading}
              boxSizing="border-box"
              width={{ sx: "100%", lg: "33%" }}
            >
              <CompactCard
                key={heading}
                icon={icon}
                title={heading}
                description={description}
                sx={{ textAlign: "left" }}
              />
            </Box>
          ))}
        </Box>
      </BackgroundImageBox>
      <Box
        backgroundColor="#E8F5E9"
        sx={{ marginBottom: { xs: "150px", lg: "100px" } }}
      >
        <ContentSection>
          <Box
            sx={{
              padding: { xs: 0, lg: 6 },
              display: "flex",
              flexDirection: { xs: "column", lg: "row" },
              justifyContent: "center",
              gap: 6,
            }}
          >
            <Box sx={{ flex: 1, flexShrink: 0 }}>
              <Typography color="#001b35" variant="h6">
                Real-World Applications
              </Typography>
              <Typography color="#001b35" variant="body1" marginBlockStart={2}>
                Flamestone’s solutions are designed to transform operations
                across industries:
              </Typography>
              <IconedItemsList
                marginBlockStart={4}
                textAlign="left"
                items={[
                  {
                    heading: "Energy Production",
                    description:
                      "Hardware and software support for energy production facilities, such as biomass, solar, wind and nuclear ensuring optimal performance and reduced emissions.",
                    icon: PowerOutlined,
                  },
                  {
                    heading: "Transportation",
                    description:
                      "AI-driven systems for greener logistics and fleet management.",
                    icon: LocalShippingOutlined,
                  },
                  {
                    heading: "Smart Infrastructure",
                    description:
                      "Data-driven tools for energy-efficient industrial and building operations.",
                    icon: HomeWorkOutlined,
                  },
                ]}
              />
            </Box>
            <Box sx={{ flex: 1, flexShrink: 0 }}>
              <img
                alt=""
                src={SolarPanels}
                style={{
                  borderRadius: 32,
                  marginBottom: "-200px",
                  height: "550px",
                  width: "100%",
                  objectFit: "cover",
                }}
              />
            </Box>
          </Box>
        </ContentSection>
      </Box>
      <ContentSection>
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", lg: "row" },
            justifyContent: "center",
            margin: "48px auto",
            gap: 6,
          }}
        >
          <Box sx={{ flex: 1, flexShrink: 0 }}>
            <Typography color="#001b35" variant="h3">
              Why Choose Flamestone?
            </Typography>
            <Typography color="#001b35" variant="subtitle2" marginTop={2}>
              Flamestone is redefining the future of energy and technology by
              integrating data-driven insights and innovative solutions into
              sustainable industries. Let us help you achieve your goals with
              smarter, greener technology.
            </Typography>
            <Box
              sx={{
                marginTop: 4,
                gap: 1,
              }}
            >
              <LinkButton to="/biogas" size="large">
                Learn about Biogas Solutions
              </LinkButton>
            </Box>
          </Box>

          <Box sx={{ flex: 1, flexShrink: 0 }}>
            <Typography color="#001b35">
              <Typography variant="body1">
                Flamestone offers a unique blend of hardware innovation,
                software customisation, and sustainability-focused solutions.
                With over 13 years of expertise and a global network, we
                provide:
              </Typography>
            </Typography>
            <IconedItemsList
              marginBlockStart={4}
              textAlign="left"
              maxWidth={500}
              items={[
                {
                  heading: "Integrated Solutions",
                  description:
                    "End-to-end support, from server design to software implementation.",
                  icon: IntegrationInstructionsOutlined,
                },
                {
                  heading: "Sustainability Commitment",
                  description:
                    "Technology that aligns with environmental goals and reduces carbon footprints.",
                  icon: EnergySavingsLeafOutlined,
                },
                {
                  heading: "Proven Track Record",
                  description:
                    "Trusted by global leaders across energy and technology sectors, including partnerships with NEOS, NWT, MGE Data, and Jablotron.",
                  icon: StarsOutlined,
                },
              ]}
            />
          </Box>
        </Box>
      </ContentSection>
    </Fragment>
  );
}

export default EnergyPage;
